import React, { useState, useEffect } from 'react';
import { History, Check, Clock, RotateCcw, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { get, post } from '@rails/request.js';
import { useParams } from 'react-router-dom';

interface FileVersion {
  id: number;
  version_number: number;
  label: string;
  description: string;
  created_at: string;
  is_current: boolean;
}

interface FileVersionSelectorProps {
  fileId: number | null;
  currentVersion?: number;
  onVersionChange?: (versionNumber: number, content?: string) => void;
}

export function FileVersionSelector({ 
  fileId, 
  currentVersion,
  onVersionChange 
}: FileVersionSelectorProps) {
  const [versions, setVersions] = useState<FileVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [versionToRestore, setVersionToRestore] = useState<number | null>(null);
  const { toast } = useToast();
  const { siteSlug } = useParams<{ siteSlug: string }>();

  useEffect(() => {
    let isMounted = true;
    
    if (fileId && isMounted) {
      loadVersions();
    }
    
    return () => {
      isMounted = false;
    };
  }, [fileId, siteSlug]);

  const loadVersions = async () => {
    if (!fileId || !siteSlug) return;
    
    setLoading(true);
    try {
      const response = await get(`/sites/${siteSlug}/theme_build_files/${fileId}/versions/list`, {
        responseKind: 'json'
      });
      
      if (!response.ok) throw new Error('Failed to load versions');
      
      const data = await response.json;
      setVersions(data.versions || []);
      
      // Set current version if available
      const current = data.versions?.find((v: FileVersion) => v.is_current);
      if (current) {
        setSelectedVersion(current.version_number);
      }
    } catch (error) {
      console.error('Error loading versions:', error);
      toast({
        title: 'Error',
        description: 'Failed to load file versions',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVersionSelect = async (versionNumber: number) => {
    setSelectedVersion(versionNumber);
    
    // Fetch the version content
    if (fileId && siteSlug) {
      try {
        // Find the version in the list to get its ID
        const version = versions.find(v => v.version_number === versionNumber);
        if (!version) return;
        
        const response = await get(`/sites/${siteSlug}/theme_build_files/${fileId}/versions/${version.id}`, {
          responseKind: 'json'
        });
        
        if (!response.ok) throw new Error('Failed to load version content');
        
        const data = await response.json;
        
        if (onVersionChange && data.version) {
          onVersionChange(versionNumber, data.version.content);
        }
      } catch (error) {
        console.error('Error loading version content:', error);
        toast({
          title: 'Error',
          description: 'Failed to load version content',
          variant: 'destructive',
        });
      }
    }
  };

  const handleRestoreClick = (versionNumber: number) => {
    setVersionToRestore(versionNumber);
    setShowRestoreDialog(true);
  };

  const handleRestoreConfirm = async () => {
    if (!fileId || !versionToRestore || !siteSlug) return;
    
    try {
      // Find the version in the list to get its ID
      const version = versions.find(v => v.version_number === versionToRestore);
      if (!version) return;
      
      const response = await post(`/sites/${siteSlug}/theme_build_files/${fileId}/versions/${version.id}/restore`, {
        responseKind: 'json'
      });
      
      if (!response.ok) throw new Error('Failed to restore version');
      
      const data = await response.json;
      
      toast({
        title: 'Version Restored',
        description: `Successfully restored to version ${versionToRestore}`,
      });
      
      // Reload versions to update current state
      await loadVersions();
      
      // Notify parent component with restored content
      if (onVersionChange && data.file) {
        onVersionChange(versionToRestore, data.file.content);
      }
    } catch (error) {
      console.error('Error restoring version:', error);
      toast({
        title: 'Error',
        description: 'Failed to restore version',
        variant: 'destructive',
      });
    } finally {
      setShowRestoreDialog(false);
      setVersionToRestore(null);
    }
  };

  if (!fileId) {
    return null;
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            disabled={loading || versions.length === 0}
          >
            <History className="h-4 w-4" />
            {loading ? (
              'Loading...'
            ) : versions.length > 0 ? (
              <>
                Version {selectedVersion || currentVersion || 1}
                <Badge variant="secondary" className="ml-1">
                  {versions.length}
                </Badge>
              </>
            ) : (
              'No versions'
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[300px]">
          <DropdownMenuLabel>File Version History</DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {loading ? (
            <div className="p-2 space-y-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : versions.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No versions available</p>
            </div>
          ) : (
            <div className="max-h-[400px] overflow-y-auto">
              {versions.map((version) => (
                <DropdownMenuItem
                  key={version.version_number}
                  className="flex items-start gap-2 p-3 cursor-pointer"
                  onSelect={(e) => {
                    e.preventDefault();
                    handleVersionSelect(version.version_number);
                  }}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm">
                        Version {version.version_number}
                      </span>
                      {version.is_current && (
                        <Badge variant="default" className="text-xs">
                          <Check className="h-3 w-3 mr-1" />
                          Current
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mb-1">
                      {version.description}
                    </p>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {new Date(version.created_at).toLocaleString()}
                    </div>
                  </div>
                  {!version.is_current && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRestoreClick(version.version_number);
                      }}
                    >
                      <RotateCcw className="h-3 w-3" />
                    </Button>
                  )}
                </DropdownMenuItem>
              ))}
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore Version</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to restore to version {versionToRestore}? This will
              replace the current file content with the content from that version.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRestoreConfirm}>
              Restore
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
